import { message } from "antd";
import {
  annotateDeepLink,
  getDeepLinks,
  removeDeepLink,
  createDeepLink,
  updateDeepLink,
  regroupDeepLinks,
  duplicateLink,
  trackDeepLinkHit,
  bookmarkDeepLink,
  unbookmarkDeepLink,
} from "pages/api-clients/deep-links";
import { AnnotatedDeepLink, DeepLinkType } from "pages/api/deep-links/types";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useQueryToasterErrors } from "./useToaster";
import { useOrg } from "./useOrg";
import { useApplicationContext } from "./useApplication";

export const toDeepLinksQueryKey = ({
  user_id,
  org_id,
  type,
}: Record<string, string | undefined>) =>
  ["deep-links", { user_id, org_id, type }] as const;

export function useDeepLinks<T = any>(
  ...args: Parameters<typeof useDeepLinksData>
) {
  const deep = useDeepLinksData<T>(...args);
  const loading =
    deep.links.isFetching ||
    deep.create.isLoading ||
    deep.remove.isLoading ||
    deep.regroup.isLoading ||
    deep.duplicate.isLoading ||
    deep.update.isLoading;
  const saved = deep.links.data?.filter((a) => a.userId);
  const popular = deep.links.data?.filter((a) => !a.userId);
  return { ...deep, loading, saved, popular };
}

export function useDeepLinksData<T = any>(
  type: DeepLinkType,
  options?: { disabled?: boolean }
) {
  const app = useApplicationContext();
  const user_id = app.user?.id;
  const user_role = app.role;
  const org_id = useOrg().id!;
  const client = useQueryClient();
  const dataKey = useMemo(
    () => toDeepLinksQueryKey({ user_id, org_id, type }),
    [user_id, org_id, type]
  );
  const links = useQuery(
    dataKey,
    async () =>
      getDeepLinks(org_id!, user_id!, type) as unknown as Promise<
        AnnotatedDeepLink<T>[]
      >,
    {
      enabled: Boolean(org_id && user_id && type && !options?.disabled),
    }
  );
  useQueryToasterErrors(links);
  const create = useMutation({
    mutationFn: async (link: {
      state: string | any;
      name: string;
      description?: string;
      thumbnail?: string;
      scope: "private" | "workspace" | "global";
      group?: string;
    }) => {
      if (user_role === "viewer") {
        message.info("Access is restricted for ready-only demo accounts");
      }
      return createDeepLink(
        org_id!,
        user_id!,
        type,
        link.state,
        link.name,
        link.scope,
        link.group,
        link.description,
        link.thumbnail
      );
    },
    onSuccess: async () => client.invalidateQueries({ queryKey: dataKey }),
  });
  const update = useMutation({
    mutationFn: async (link: {
      id: string;
      name?: string;
      description?: string;
      thumbnail?: string;
      group?: string;
      state?: string | any;
      scope?: "private" | "workspace" | "global";
    }) => {
      if (user_role === "viewer") {
        message.info("Access is restricted for ready-only demo accounts");
      }
      const isJson = link.state && typeof link.state !== "string";
      const state = isJson ? "" : link.state;
      const json = isJson ? link.state : null;
      const data = (
        client.getQueryData<AnnotatedDeepLink[]>(dataKey) || []
      ).map((link_) =>
        link_.id === link.id
          ? {
              ...link_,
              orgId: link.scope === "global" ? undefined : org_id,
              userId: link.scope === "private" ? user_id : undefined,
              name: link.name || link_.name,
              description: link.description || link_.description,
              thumbnail: link.thumbnail || link_.thumbnail,
              state: state || link_.state,
              json: json || link_.json,
              group: link.group || link_.group,
            }
          : link_
      );
      client.setQueryData(dataKey, data);
      return updateDeepLink(org_id!, user_id!, link);
    },
    onError: async () => client.invalidateQueries({ queryKey: dataKey }),
  });
  const annotate = useMutation({
    mutationFn: async (link: {
      id: string;
      targetRole: string;
      useCase: string;
    }) => {
      return annotateDeepLink(link).then(() => {
        const data = (
          client.getQueryData<AnnotatedDeepLink[]>(dataKey) || []
        ).map((link_) =>
          link_.id === link.id
            ? {
                ...link_,
                targetRole: link.targetRole,
                useCase: link.useCase,
              }
            : link_
        );
        client.setQueryData(dataKey, data);
      });
    },
    onError: async () => client.invalidateQueries({ queryKey: dataKey }),
  });
  const remove = useMutation({
    mutationFn: async (link: { id: string }) => {
      if (user_role === "viewer") {
        message.info("Access is restricted for ready-only demo accounts");
      }
      const data = (
        client.getQueryData<AnnotatedDeepLink[]>(dataKey) || []
      ).filter(({ id }) => link.id !== id);
      client.setQueryData(dataKey, data);
      return removeDeepLink(link.id, org_id!, user_id!);
    },
    onError: async () => client.invalidateQueries({ queryKey: dataKey }),
  });
  const regroup = useMutation({
    mutationFn: async (args: { fromGroup: string; toGroup: string }) => {
      if (user_role === "viewer") {
        message.info("Access is restricted for ready-only demo accounts");
      }
      const link = await regroupDeepLinks(
        org_id!,
        user_id!,
        args.fromGroup,
        args.toGroup
      );
      client.invalidateQueries({ queryKey: dataKey });
      return link;
    },
  });
  const duplicate = useMutation({
    mutationFn: async (args: { id: string }) => {
      if (user_role === "viewer") {
        message.info("Access is restricted for ready-only demo accounts");
      }
      const link = await duplicateLink(args.id, org_id!, user_id!);
      client.invalidateQueries({ queryKey: dataKey });
      return link;
    },
  });
  const trackHit = useMutation({
    mutationFn: async (args: { id: string }) => {
      await trackDeepLinkHit(args.id, org_id!);
    },
  });
  const addBookmark = useMutation({
    mutationFn: async (args: { id: string }) => {
      await bookmarkDeepLink(args.id, org_id!, user_id!);
      client.invalidateQueries({ queryKey: dataKey });
    },
  });
  const removeBookmark = useMutation({
    mutationFn: async (args: { id: string }) => {
      await unbookmarkDeepLink(args.id, org_id!, user_id!);
      client.invalidateQueries({ queryKey: dataKey });
    },
  });
  return {
    annotate,
    links,
    create,
    remove,
    update,
    regroup,
    duplicate,
    trackHit,
    addBookmark,
    removeBookmark,
  };
}
